"use client";
import { Button, Card, Flex, Form, Spin, Steps, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../../assets/styles/Bond.css";
import cnt from "../../services/definedConstant";
import { BOM } from "../../services/definedMethod";
import {
  apiConfig,
  labelConfig,
  staticConfig,
} from "../customer/customer.config";
import { showCheckerApprovalModal } from "../../components/CheckerModal";
import CollateralOverview from "./components/CollateralOverview";
import OwnershipDetails from "./components/OwnershipDetails";
import CollateralClassification from "./components/CollateralClassification";
import CollateralValuation from "./components/CollateralValuation";
import CollateralPreview from "./components/Preview";
import MainLayout from "../../components/layouts/enhancement/MainLayout";
import TopContent from "../../components/layouts/enhancement/TopContent";
import FeedbackModal from "../../components/FeedbackModal";

const { Text } = Typography;

const NewCollateral = (props) => {
  const { userData } = props;
  const { session } = userData;

  const navigate = useNavigate();
  const [form] = useForm();

  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [ownershipTypeOptions, setOwnershipTypeOptions] = useState([]);
  const [collateralTypeOptions, setCollateralTypeOptions] = useState([]);
  const [exposureTypeOptions, setExposureTypeOptions] = useState([]);
  const [valuationMethodOptions, setValuationMethodOptions] = useState([]);
  const [loanCollateralOptions, setLoanCollateralOptions] = useState([]);
  const [isStepDisabled, setIsStepDisabled] = useState(true);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const searchParam = Form.useWatch("cifNumber", form);

  const dataSections = [
    {
      name: "Customer Name",
      labelValue: searchData?.entityName,
    },
    {
      name: "Customer Type",
      labelValue: searchData?.entityType,
    },
    {
      name: "Mobile Number",
      labelValue: searchData?.entityMobileNo,
    },
    {
      name: "Customer ID",
      labelValue: searchData?.entityId,
    },
  ];

  const [formData, setFormData] = useState({
    collateralOverview: null,
    ownershipDetails: null,
    classification: null,
    valuation: null,
  });

  const onSearch = () => {
    if (!searchParam.trim()) {
      BOM.AlertMsg(cnt.WARNING, "Search parameter is required");
      return;
    }
    const params = {
      exactMatch: true,
      query: searchParam.trim(),
      resource: "clients,clientIdentifiers",
    };
    const url = apiConfig.Search;
    let body = {
      session,
      params,
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res;
        let tempDetails = [];

        objList?.forEach((element) => {
          if (String(element?.entityAccountNo) === searchParam.trim()) {
            tempDetails.push({
              entityAccountNo: element.entityAccountNo
                ? element.entityAccountNo
                : "",
              entityId: element.entityId ? element.entityId : "",
              entityMobileNo: element.entityMobileNo
                ? element.entityMobileNo
                : "",
              entityName: element.entityName ? element.entityName : "",
              entityType: element.customerType ? element.customerType : "",
            });
          }
        });

        if (tempDetails[0] === undefined) {
          BOM.AlertMsg(cnt.DANGER, "No matching client found.");
          setSearchData(null);
          setIsLoading(false);
          return;
        }
        setIsLoading(false);
        setSearchData(tempDetails[0]);
        getTemplate(tempDetails[0]?.entityId);
      }
    });
  };

  function getTemplate(clientId) {
    let body = {
      session,
    };

    const url = `${apiConfig.Clients}/${clientId}/collaterals/template`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err);
        setIsLoading(false);
      } else {
        const objList = res;

        let tempCurrency = [];
        let tempOwnershipTypeOptions = [];
        let tempCollateralTypeOptions = [];
        let tempExposureTypeOptions = [];
        let tempValuationMethodOptions = [];
        let loanCollateralOptions = [];

        objList?.currencyOptions?.forEach((element) => {
          tempCurrency.push({
            value: element.code,
            label: element.name,
            symbol: element.displaySymbol,
          });
        });

        objList?.ownershipTypeOptions?.forEach((element) => {
          tempOwnershipTypeOptions.push({
            value: element.id,
            label: element.value,
          });
        });

        objList?.collateralTypeOptions?.forEach((element) => {
          tempCollateralTypeOptions.push({
            value: element.id,
            label: element.value,
          });
        });

        objList?.exposureTypeOptions?.forEach((element) => {
          tempExposureTypeOptions.push({
            value: element.id,
            label: element.value,
          });
        });

        objList?.valuationMethodOptions?.forEach((element) => {
          tempValuationMethodOptions.push({
            value: element.id,
            label: element.value,
          });
        });

        objList?.loanCollateralTemplateDataList?.forEach((element) => {
          loanCollateralOptions.push({
            value: element.id,
            label: element.value,
          });
        });

        setCurrencyOptions(tempCurrency);
        setOwnershipTypeOptions(tempOwnershipTypeOptions);
        setCollateralTypeOptions(tempCollateralTypeOptions);
        setExposureTypeOptions(tempExposureTypeOptions);
        setValuationMethodOptions(tempValuationMethodOptions);
        setLoanCollateralOptions(loanCollateralOptions);
        setIsLoading(false);
      }
    });
  }

  const handleClose = () => {
    setSuccessModalVisible(false);
    setWarningModalVisible(false);
    navigate("/manage-collateral", { replace: false });
  };

  const stepTitles = [
    "Collateral Overview",
    "Ownership Details",
    "Collateral Classification",
    "Collateral Valuation",
    "Preview",
  ];

  const onChange = (value) => {
    setCurrent(value);
  };

  const nextButtonClick = () => {
    form.submit();
  };

  const prevButtonClick = () => {
    setCurrent(current - 1);
  };

  function onCancel() {
    navigate("/manage-collateral", { replace: false });
  }

  const onFinishCollateralOverviewForm = (values) => {
    values.registrationDate = values.registrationDate
      ? BOM.DateTime(values.registrationDate, "only-date")
      : values.registrationDate;

    setFormData({
      ...formData,
      collateralOverview: values,
    });

    setCurrent(current + 1);
  };

  const onFinishOwnershipDetailsForm = (values) => {
    setFormData({
      ...formData,
      ownershipDetails: values,
    });
    setCurrent(current + 1);
  };

  const onFinishClassificationForm = (values) => {
    setFormData({
      ...formData,
      classification: values,
    });
    setCurrent(current + 1);
  };

  const onFinishValuationForm = (values) => {
    setFormData({
      ...formData,
      valuation: values,
    });
    setCurrent(current + 1);
  };

  const forms = [
    <CollateralOverview
      onFinish={onFinishCollateralOverviewForm}
      current={current}
      setCurrent={setCurrent}
      initialValues={formData.collateralOverview}
      form={form}
      dataSections={dataSections}
      searchData={searchData}
      onSearch={onSearch}
      searchParam={searchParam}
      isEdit={true}
    />,
    <OwnershipDetails
      onFinish={onFinishOwnershipDetailsForm}
      current={current}
      setCurrent={setCurrent}
      initialValues={formData.ownershipDetails}
      form={form}
      ownershipTypeOptions={ownershipTypeOptions}
      entityId={searchData?.entityId}
    />,
    <CollateralClassification
      onFinish={onFinishClassificationForm}
      current={current}
      setCurrent={setCurrent}
      initialValues={formData.classification}
      form={form}
      collateralTypeOptions={collateralTypeOptions}
      exposureTypeOptions={exposureTypeOptions}
      loanCollateralOptions={loanCollateralOptions}
    />,
    <CollateralValuation
      onFinish={onFinishValuationForm}
      current={current}
      setCurrent={setCurrent}
      initialValues={formData.valuation}
      form={form}
      currencyOptions={currencyOptions}
      valuationMethodOptions={valuationMethodOptions}
    />,
    <CollateralPreview
      formData={formData}
      currencySymbol=""
      ownershipTypeOptions={ownershipTypeOptions}
      collateralTypeOptions={collateralTypeOptions}
      exposureTypeOptions={exposureTypeOptions}
      loanCollateralOptions={loanCollateralOptions}
      currencyOptions={currencyOptions}
      valuationMethodOptions={valuationMethodOptions}
    />,
  ];

  const isLastStep = current === forms.length - 1;

  const uploadStagedDocuments = async () => {
    const documents = formData.ownershipDetails?.documents || [];
    const stagedDocuments = documents.filter((file) => file.originFileObj);

    if (!stagedDocuments.length) return;

    await Promise.all(
      stagedDocuments.map((file) => {
        const data = new FormData();
        data.append("file", file.originFileObj);
        data.append("name", file.name.replace(/\.[^/.]+$/, ""));

        return axios({
          method: "POST",
          url: `${apiConfig.Clients}/${searchData?.entityId}/documents`,
          data,
          headers: {
            Authorization: session,
            "Finsys-Platform-Tenantid": "default",
          },
        });
      }),
    );
  };

  const onSubmit = () => {
    let formValues = {
      ...formData.collateralOverview,
      ...formData.ownershipDetails,
      ...formData.classification,
      ...formData.valuation,
    };

    formValues.registrationDate = formValues.registrationDate
      ? BOM.DateTime(formValues.registrationDate, "only-date")
      : formValues.registrationDate;

    delete formValues.cifNumber;
    delete formValues.documents;
    delete formValues.documentIds;

    console.log(formValues);

    const body = {
      ...formValues,
      locale: "en",
      dateFormat: "dd MMMM yyyy",
      method: "POST",
      session,
    };

    const url = `${apiConfig.Clients}/${searchData?.entityId}/collaterals`;

    setIsLoading(true);
    BOM.FetchReqAction(
      body,
      url,
      (err, res) => {
        if (err) {
          let message =
            err.errors?.[0]?.defaultUserMessage || "An error occurred";

          BOM.AlertMsg(cnt.DANGER, message);
          setIsLoading(false);
        } else {
          uploadStagedDocuments()
            .catch((error) => {
              console.error("Error uploading documents:", error);
              BOM.AlertMsg(
                cnt.WARNING,
                "Collateral was created, but document upload failed",
              );
            })
            .finally(() => {
              setSuccessModalVisible(true);
              setIsLoading(false);
            });
        }
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: () => {
          setIsLoading(false);
          navigate("/manage-collateral");
        },
      },
    );
  };

  return (
    <MainLayout
      pageItems={staticConfig.newCollateralBreadcrumbItems}
      onSave={onSubmit}
    >
      <TopContent
        pageTitle={labelConfig.newCollateral}
        createPageTitle={labelConfig.createCollateral}
      />
      <Spin spinning={isLoading} size="large">
        <div
          style={{
            display: "flex",
            paddingLeft: "44px",
            marginTop: "20px",
            width: "100%",
          }}
        >
          {/* Navigation section */}
          <div style={{ width: "288px", padding: "20px 0" }}>
            <Steps
              className="custom-step-style"
              current={current}
              onChange={onChange}
              direction="vertical"
              style={{ height: "723px" }}
              items={[
                {
                  title: <Text strong>Collateral Overview</Text>,
                  disabled: isStepDisabled,
                },
                {
                  title: <Text strong>Ownership Details</Text>,
                  disabled: isStepDisabled,
                },
                {
                  title: <Text strong>Collateral Classification</Text>,
                  disabled: isStepDisabled,
                },
                {
                  title: <Text strong>Collateral Valuation</Text>,
                  disabled: isStepDisabled,
                },
                {
                  title: <Text strong>Preview</Text>,
                  disabled: isStepDisabled,
                },
              ]}
            />
          </div>

          {/* Form section */}
          <Card
            style={{
              borderRadius: 20,
              width: "100%",
              margin: 0,
              display: "flex",
              flexDirection: "column",
              padding: "0 20px",
              height: `${current === forms.length - 1 ? "723px" : "100%"}`,
              overflowY: `${current === forms.length - 1 ? "auto" : "hidden"}`,
            }}
          >
            <div style={{ flexGrow: 1 }}>{forms[current]}</div>

            {/* Button Section (at bottom) */}
            <div
              style={{
                paddingTop: 30,
                borderRadius: 20,
              }}
            >
              <Flex justify="end" gap="30px" align="end">
                {current === 0 && (
                  <Button
                    style={{
                      backgroundColor: "white",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      padding: "20px 15px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    colortext="#344054"
                    className="previous-button"
                    onClick={() => setWarningModalVisible(true)}
                  >
                    Go Back
                  </Button>
                )}
                {current > 0 && (
                  <Button
                    style={{
                      backgroundColor: "white",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      padding: "20px 15px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    colortext="#344054"
                    className="previous-button"
                    onClick={prevButtonClick}
                  >
                    Previous
                  </Button>
                )}
                {!isLastStep && (
                  <Button
                    style={{
                      backgroundColor: "#15CCAE",
                      color: "white",
                      border: "1px solid #15CCAE",
                      borderRadius: 8,
                      padding: "20px 15px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    onClick={nextButtonClick}
                  >
                    Next - {stepTitles[current + 1]}
                  </Button>
                )}
                {isLastStep && (
                  <Button
                    style={{
                      backgroundColor: "#15CCAE",
                      color: "white",
                      border: "1px solid #15CCAE",
                      borderRadius: 8,
                      padding: "20px 15px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    onClick={onSubmit}
                  >
                    Create Collateral
                  </Button>
                )}
              </Flex>
            </div>
          </Card>
        </div>
      </Spin>

      {/* Success Modal */}
      <FeedbackModal
        visible={successModalVisible}
        type="success"
        onPrimaryAction={handleClose}
        onSecondaryAction={() => {
          onCancel();
        }}
        onClose={handleClose}
        message="Collateral has been successfully created."
      />

      {/* Warning Modal */}
      <FeedbackModal
        visible={warningModalVisible}
        type="warning"
        onPrimaryAction={() => {
          onCancel();
        }}
        onSecondaryAction={() => {
          handleClose();
        }}
        onClose={handleClose}
      />
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(NewCollateral);
